/**
 * @lines98/client/lib/pending-own-row — оптимистичная СВОЯ строка только что
 * отправленной партии доски дня, для МГНОВЕННОГО показа в лидерборде без round-trip
 * (brief-44 §B P2 — «свежесыгранная партия в лидерборде сразу»).
 *
 * ЗАЧЕМ / WHY THIS EXISTS:
 * SWR (instant-hydration §3.1) ревалидирует борд на mount/focus/visibility, но НЕ ловит
 * событие «мой сабмит»: игрок доиграл доску дня → перешёл в лидерборд → его свежий
 * результат там появлялся лишь после следующего fetch (а на узком/медленном — с заметной
 * задержкой/мерцанием «нет вас в списке»). Этот модуль записывает СВОЮ строку из ответа
 * `POST /finish-game` (server-truth `me{rank,score,short_id,...}` — P1) в момент зачёта;
 * экран лидерборда при mount подсаживает её ОПТИМИСТИЧНО поверх снимка/сервера, а SWR
 * reconcile сходится с серверной правдой (идемпотентная подмена по сигнатуре — без
 * мерцания, leaderboard-screen).
 *
 * **Ствол → ветка.** Это тонкая ветка над общим `lib/screen-snapshot` (тот же per-player
 * snapshot-движок, что `meta-snapshot` / `leaderboard-snapshot`) — НЕ второй кэш-движок.
 * Persistence / изоляция по playerId / версионирование / type-guard — из ствола.
 *
 * **Server-truth / анти-чит цел.** Запись — ВЕРБАТИМ серверный `me` (rank — серверная
 * правда, P1; клиент его не выдумывает). Это UX-проекция для показа: истина — следующий
 * fetch (reconcile перетирает). Ничего не верифицируется/не начисляется; движок не
 * импортирует. `short_id` — свой реплей (own-replay carve-out §3A.2), не чужая видимость.
 *
 * **Ключ — playerId (ствол) + дата дня (внутри payload).** Запись валидна только для ТОЙ
 * даты доски дня, что отправлена. На следующий день (другой `date_of_day`) запись
 * считается устаревшей и игнорируется (rollover-safe; экран сверяет дату). Гость → no-op
 * (ствол не пишет без playerId).
 *
 * Cross-references:
 *   - lib/screen-snapshot.ts (ствол: per-player снимок — load/save/clear).
 *   - lib/leaderboard-snapshot.ts (параллельная ветка: последняя страница борда).
 *   - services/game-submit.ts (источник `me` — SubmitResult.me, server-truth P1).
 *   - screens/leaderboard-screen.ts (consumer: подсадка оптимистичной строки + reconcile).
 *   - screens/board-screen.ts (producer: запись после verified daily submit).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §3.
 */

import type { DailyOwnRow } from '@lines98/shared/types/game';

import { createScreenSnapshot } from './screen-snapshot.js';

// =============================================================================
// Payload — серверная своя строка + дата дня (для rollover-инвалидации)
// =============================================================================

/**
 * Записанная своя строка только что отправленной доски дня. Несёт ровно серверный
 * `me` (P1) + `dateOfDay` (для какой доски валидна) + `ts` (отладка/возможная TTL).
 */
export interface PendingOwnRow {
  /** Доска дня, к которой относится строка (YYYY-MM-DD). На другой день → устарела. */
  readonly dateOfDay: string;
  /** Серверная своя строка (rank + own short_id + verified score/lines/moves). */
  readonly row: DailyOwnRow;
  /** ms epoch записи (отладка; показ от него не зависит). */
  readonly ts: number;
}

// =============================================================================
// Снимок-«ствол» (per-player изоляция + версия + persistence)
// =============================================================================

const KEY_PREFIX = 'lines98.daily.pending-own-row.v1.';
/** Версия схемы payload (несовпадение → отбрасываем; UX-кэш, безвреден). */
const SCHEMA_VERSION = 1;

const store = createScreenSnapshot<PendingOwnRow>({
  keyPrefix: KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: isPendingOwnRow,
});

// =============================================================================
// Public API
// =============================================================================

/**
 * Записать СВОЮ строку только что зачтённой доски дня (из ответа finish-game, P1).
 * No-op для гостя (ствол не пишет без playerId). Перезаписывает прежнюю запись
 * (последний сабмит = актуальная строка).
 *
 * @param dateOfDay — доска дня, которую отправили (YYYY-MM-DD).
 * @param row — серверный `me` (server-truth).
 * @param now — ms epoch (инжектируемые часы; по умолчанию `Date.now()`).
 */
export function savePendingOwnRow(dateOfDay: string, row: DailyOwnRow, now: number = Date.now()): void {
  store.save({ dateOfDay, row, ts: now });
}

/**
 * Прочитать СВОЮ оптимистичную строку для `dateOfDay` (СИНХРОННО, для mount). Возвращает
 * `null`, если: записи нет, дата НЕ совпадает (устарела на rollover), гость / нет storage.
 * Чистый read — server-truth остаётся истиной (reconcile перетрёт).
 *
 * @param dateOfDay — доска дня, которую рисует экран (today). Несовпадение → null.
 */
export function loadPendingOwnRow(dateOfDay: string): DailyOwnRow | null {
  const rec = store.load();
  if (!rec) return null;
  if (rec.dateOfDay !== dateOfDay) return null; // другая доска (rollover) → игнор.
  return rec.row;
}

/** Удалить запись текущего игрока (явный сброс / тесты). No-op для гостя. */
export function clearPendingOwnRow(): void {
  store.clear();
}

// =============================================================================
// Type guard
// =============================================================================

function isDailyOwnRow(value: unknown): value is DailyOwnRow {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Number.isFinite(v['rank']) &&
    typeof v['short_id'] === 'string' &&
    Number.isFinite(v['score_verified']) &&
    Number.isFinite(v['lines_verified']) &&
    Number.isFinite(v['moves_count']) &&
    typeof v['finished_at'] === 'string'
  );
}

function isPendingOwnRow(value: unknown): value is PendingOwnRow {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['dateOfDay'] === 'string' &&
    typeof v['ts'] === 'number' &&
    isDailyOwnRow(v['row'])
  );
}
